import * as Accordion from "@radix-ui/react-accordion"
import clsx from "clsx"
import { ReactNode, useEffect, useRef } from "react"

import { faIcon } from "@utils/faIcon"

import { Icon } from "@atoms/index"

import { AlertBanner } from "@molecules/index"

import styles from "./PreviewModule.module.scss"
import { PreviewModuleProps } from "./PreviewModule.types"

/**
 * Wrapper for Accordion.Content that fixes a known Radix issue where
 * the accordion height doesn't update when children change dynamically.
 * Uses ResizeObserver to manually update the CSS variable height.
 */
function AccordionContent({ children, className }: { children: ReactNode; className?: string }) {
    const innerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const inner = innerRef.current
        const content = contentRef.current

        if (!inner || !content) {
            return
        }

        const resizeObserver = new ResizeObserver(() => {
            const currentHeight = inner.clientHeight
            content.style.setProperty("--radix-accordion-content-height", `${currentHeight}px`)
        })

        resizeObserver.observe(inner)

        return () => resizeObserver.disconnect()
    }, [])

    return (
        <Accordion.Content ref={contentRef} className={className}>
            <div ref={innerRef}>{children}</div>
        </Accordion.Content>
    )
}

export default function PreviewModule(props: PreviewModuleProps) {
    const {
        label,
        mode = "default",
        defaultOpen = false,
        indicator,
        cta1,
        cta2,
        hasAlert,
        alertBanner,
        className,
        children,
        contentClassName,
        isOpen,
        onOpenChange,
        ...rest
    } = props

    const baseClassName = clsx(styles.base, className, { [styles.alert]: hasAlert })

    const isControlled = isOpen !== undefined && onOpenChange !== undefined

    const accordionValue = isOpen ? "module" : ""
    const handleAccordionChange = (value: string) => {
        if (onOpenChange) {
            onOpenChange(value === "module")
        }
    }

    if (mode === "accordion") {
        return (
            <div className={baseClassName} {...rest}>
                {hasAlert ? <AlertBanner type="banner" status="warning" {...alertBanner} /> : null}

                <Accordion.Root
                    type="single"
                    collapsible={true}
                    value={isControlled ? accordionValue : undefined}
                    onValueChange={isControlled ? handleAccordionChange : undefined}
                    defaultValue={defaultOpen && !isControlled ? "module" : undefined}
                >
                    <Accordion.Item value="module" className={styles.item}>
                        <Accordion.Trigger className={clsx(styles.header, styles.headerClickable)}>
                            <div className={styles.labelAndMeta}>
                                <Icon
                                    size={14}
                                    container="square"
                                    icon={faIcon({ name: "caret-down", style: "solid" })}
                                    className={styles.chevron}
                                />
                                {label}
                                {indicator}
                            </div>
                            <div className={styles.actions} onClick={(event) => event.stopPropagation()}>
                                {cta1}
                                {cta2}
                            </div>
                        </Accordion.Trigger>
                        <AccordionContent className={clsx(styles.content, contentClassName)}>
                            {children}
                        </AccordionContent>
                    </Accordion.Item>
                </Accordion.Root>
            </div>
        )
    } else if (mode === "headless") {
        return (
            <div className={baseClassName} {...rest}>
                {hasAlert ? <AlertBanner type="banner" status="warning" {...alertBanner} /> : null}
                <div className={clsx(styles.content, contentClassName)}>{children}</div>
            </div>
        )
    } else {
        return (
            <div className={baseClassName} {...rest}>
                {hasAlert ? <AlertBanner type="banner" status="warning" {...alertBanner} /> : null}

                <div className={styles.header}>
                    <div className={styles.labelAndMeta}>
                        {label}
                        {indicator}
                    </div>
                    <div className={styles.actions}>
                        {cta1}
                        {cta2}
                    </div>
                </div>
                <div className={clsx(styles.content, contentClassName)}>{children}</div>
            </div>
        )
    }
}
